import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Video, Lock, Clock, Hourglass, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SessionTools } from "../components/SessionTools";
import { ConsultationDocs } from "../components/ConsultationDocs";

function loadDaily() {
  return new Promise((resolve, reject) => {
    if (window.DailyIframe) return resolve(window.DailyIframe);
    const s = document.createElement("script");
    s.src = "https://unpkg.com/@daily-co/daily-js";
    s.onload = () => resolve(window.DailyIframe);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

function fmtCountdown(ms) {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function VisioSession() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [pin, setPin] = useState("");
  const [joined, setJoined] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);
  const frameRef = useRef(null);
  const callRef = useRef(null);
  const videoWrapRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFs(document.fullscreenElement === videoWrapRef.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const goFullscreen = () => {
    const el = videoWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) { document.exitFullscreen?.(); }
    else { (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el); }
  };

  useEffect(() => {
    api.get(`/visio/session/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [token]);

  const startFrame = async (data) => {
    setWaiting(null);
    setJoined(data);
    if (!data.simulated && data.room_url) {
      const Daily = await loadDaily();
      const call = Daily.createFrame(frameRef.current, {
        showLeaveButton: true, iframeStyle: { width: "100%", height: "620px", border: "0", borderRadius: "16px" },
      });
      await call.join({ url: data.room_url, token: data.token });
      callRef.current = call;
    }
  };

  const join = async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const { data } = await api.post(`/visio/session/${token}/join`, { pin });
      if (data.waiting) { setWaiting(data); }
      else { await startFrame(data); }
    } catch (e) {
      if (!silent) toast.error(apiError(e, "PIN incorrect ou session indisponible"));
    } finally { if (!silent) setBusy(false); }
  };

  // Connexion automatique (bouton "Rejoindre" depuis l'espace client / tableau de bord)
  useEffect(() => {
    if (info && new URLSearchParams(window.location.search).get("auto") === "1" && !joined && !waiting) {
      setBusy(true);
      api.get(`/visio/session/${token}/autojoin`)
        .then(({ data }) => { if (data.waiting) setWaiting(data); else startFrame(data); })
        .catch(() => toast.info("Saisissez votre code PIN pour rejoindre la session."))
        .finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  // Salle d'attente : reconnexion automatique dès que la salle s'ouvre
  useEffect(() => {
    if (!waiting) return;
    const opensAt = new Date(waiting.opens_at).getTime();
    const tick = setInterval(() => {
      if (Date.now() >= opensAt) join(true);
    }, 15000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  useEffect(() => () => { try { callRef.current?.destroy(); } catch { } }, []);

  // Salle d'attente : signale la présence du patient au médecin (heartbeat)
  useEffect(() => {
    if (!joined && !waiting) return;
    const ping = () => api.post(`/visio/session/${token}/heartbeat`).catch(() => {});
    ping();
    const id = setInterval(ping, 20000);
    return () => clearInterval(id);
  }, [joined, waiting, token]);

  if (info === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;
  if (info === false) return <div className="mx-auto max-w-lg px-4 py-20 text-center text-slate-500" data-testid="visio-notfound">Session introuvable ou expirée.</div>;

  const startMs = new Date(info.start).getTime();
  const remainToStart = startMs - nowTs;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10" data-testid="visio-session">
      <div className="mb-4 flex justify-end">
        <Button data-testid="visio-end" onClick={() => { try { callRef.current?.destroy(); } catch {} window.close(); setTimeout(() => { window.location.href = "/"; }, 300); }}
          variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">
          Mettre fin à la présentation
        </Button>
      </div>
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-500">
        <ShieldCheck size={16} className="shrink-0 text-emerald-500" /> {info.confidentialite}
      </div>
      <h1 className="font-display text-xl font-bold text-navy">{info.type_label}</h1>
      <p className="text-sm text-slate-500">
        {info.name}
        {info.immediate
          ? <span> · <span className="font-semibold text-emerald-600">Consultation immédiate</span></span>
          : <span> · {new Date(info.start).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</span>}
      </p>

      {waiting ? (
        <div className="mx-auto mt-8 max-w-lg rounded-2xl border border-ciel/30 bg-gradient-to-b from-sky-50 to-white p-10 text-center shadow-sm" data-testid="visio-waiting-panel">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ciel/10">
            <Hourglass size={30} className="animate-pulse text-ciel" />
          </div>
          <p className="mt-5 font-display text-lg font-semibold text-navy" data-testid="visio-waiting-title">
            Le praticien n'a pas encore ouvert la salle
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
            Vous êtes bien connecté(e). Votre médecin vous rejoindra à l'heure prévue. Merci de patienter, la salle s'ouvrira automatiquement.
          </p>
          <div className="mx-auto mt-6 inline-flex items-center gap-3 rounded-full border border-ciel/30 bg-white px-6 py-3">
            <Clock size={20} className="text-ciel" />
            <span className="text-xs uppercase tracking-wide text-slate-400">Votre médecin vous rejoindra dans</span>
            <span className="font-display text-2xl font-bold tabular-nums text-navy" data-testid="visio-waiting-countdown">
              {fmtCountdown(remainToStart)}
            </span>
          </div>
          <p className="mt-6 text-xs text-slate-400">
            Reconnexion automatique en cours… vous n'avez rien à faire.
          </p>
        </div>
      ) : !joined ? (
        <div className="mx-auto mt-8 max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="visio-pin-panel">
          <Lock size={32} className="mx-auto text-navy" />
          <p className="mt-3 text-sm text-slate-600">Saisissez votre code PIN à 4 chiffres pour rejoindre.</p>
          <Input data-testid="visio-pin" value={pin} maxLength={4} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            className="mt-4 text-center font-display text-2xl tracking-[0.4em]" placeholder="••••" />
          <Button data-testid="visio-join" onClick={() => join(false)} disabled={busy || pin.length !== 4} className="mt-4 w-full rounded-full bg-ciel text-white hover:bg-sky-700">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Rejoindre la session"}
          </Button>
        </div>
      ) : joined.simulated ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-3" data-testid="visio-live">
          <div className="lg:col-span-2">
            <div className="mb-2 flex justify-end">
              <Button size="sm" variant="outline" data-testid="visio-fullscreen" onClick={goFullscreen} className="rounded-full text-xs">
                <Maximize2 size={13} className="mr-1" /> Plein écran
              </Button>
            </div>
            <div ref={videoWrapRef} className="relative rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-12 text-center" data-testid="visio-simulated">
              {isFs && (
                <div className="pointer-events-none absolute left-1/2 top-4 z-50 -translate-x-1/2 animate-pulse rounded-full bg-black/70 px-4 py-1.5 text-xs font-medium text-white shadow-lg" data-testid="visio-fs-hint">
                  Appuyez sur <kbd className="rounded bg-white/20 px-1.5 py-0.5 font-mono">Échap</kbd> pour quitter le plein écran
                </div>
              )}
              <Video size={40} className="mx-auto text-slate-400" />
              <p className="mt-4 font-display text-lg font-semibold text-navy">Salle de visioconférence (mode simulé)</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">La connexion PIN a réussi. La vidéo réelle s'activera dès que la clé API Daily.co sera configurée. Salle : <code className="text-xs">{joined.room_url.split("/").pop()}</code></p>
            </div>
          </div>
          <div className="space-y-6 lg:col-span-1">
            <ConsultationDocs mode="patient" token={token} />
            <SessionTools token={token} />
          </div>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-3" data-testid="visio-live">
          <div className="lg:col-span-2">
            <div className="mb-2 flex justify-end">
              <Button size="sm" variant="outline" data-testid="visio-fullscreen" onClick={goFullscreen} className="rounded-full text-xs">
                <Maximize2 size={13} className="mr-1" /> Plein écran
              </Button>
            </div>
            <div ref={videoWrapRef} className="relative overflow-hidden rounded-2xl bg-black/5">
              {isFs && (
                <div className="pointer-events-none absolute left-1/2 top-4 z-50 -translate-x-1/2 animate-pulse rounded-full bg-black/70 px-4 py-1.5 text-xs font-medium text-white shadow-lg" data-testid="visio-fs-hint">
                  Appuyez sur <kbd className="rounded bg-white/20 px-1.5 py-0.5 font-mono">Échap</kbd> pour quitter le plein écran
                </div>
              )}
              <div ref={frameRef} className="min-h-[620px] w-full rounded-2xl [&_iframe]:!h-full" data-testid="visio-frame" />
            </div>
          </div>
          <div className="space-y-6 lg:col-span-1">
            <ConsultationDocs mode="patient" token={token} />
            <SessionTools token={token} />
          </div>
        </div>
      )}
    </div>
  );
}
